import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useT } from "@/i18n/LanguageContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";

const fmt = (s) => (s ? String(s).slice(0, 16).replace("T", " ") : "—");

// Two Service-Desk credentials share this card: `readonly` (lookups only) and `filing` (add_order_email + lookups).
const KINDS = {
  readonly: {
    path: "/mcp-connector/readonly", icon: "🔑", title: "Read-only credential", sub: "Service Desk", defaultLabel: "Service Desk",
    blurb: "A separate credential for the Leprecon Service Desk: order lookups only (lookup_order, search, fetch). Writing notes is refused on every call. Sent as a Bearer header to a token-free URL — never in a link.",
    createLabel: "Create read-only credential", dialogTitle: "Read-only credential — shown once",
    confirmCreate: "Create a read-only credential? It is shown once and grants order lookups to whoever holds it.",
    confirmRevoke: "Revoke the read-only credential? The Service Desk loses access immediately.", revokedToast: "Read-only credential revoked",
  },
  filing: {
    path: "/mcp-connector/filing", icon: "📨", title: "E-mail filing credential (write)", sub: "Service Desk → Emails tab", defaultLabel: "Service Desk — e-mail filing",
    blurb: "Write-scoped credential for the Service Desk: every e-mail SDC sends to a customer is filed under the matching order (Emails tab, direction Sent) via the add_order_email tool — idempotent on SDC's external_id, order_not_found when the order does not exist. tools/list on this token returns only add_order_email + the read tools (never add_order_note).",
    createLabel: "Create e-mail filing credential", dialogTitle: "E-mail filing credential — shown once",
    confirmCreate: "Create the e-mail filing credential? It is shown once and lets the Service Desk write sent e-mails into orders.",
    confirmRevoke: "Revoke the e-mail filing credential? SDC stops filing e-mails immediately (its queue keeps them as waiting).", revokedToast: "E-mail filing credential revoked",
  },
};

function TokenOnceDialog({ secret, onClose, t, cfg, kind }) {
  const copy = async () => { try { await navigator.clipboard.writeText(secret.token); toast.success(t("Credential copied")); } catch { toast.error(t("Copy failed — select it manually")); } };
  const view = secret?.[kind];
  return (
    <Dialog open={!!secret} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg" data-testid={`${kind}-token-dialog`}>
        <DialogHeader><DialogTitle>{cfg.icon} {t(cfg.dialogTitle)}</DialogTitle></DialogHeader>
        <p className="text-sm text-slate-700">{t("Store it in the Service Desk secret store now. It cannot be displayed again — revoke and create a new one if lost.")}</p>
        <code className="block text-xs bg-slate-50 border border-slate-300 rounded px-2 py-2 break-all" data-testid={`${kind}-token-value`}>{secret?.token}</code>
        <div className="text-[11px] text-slate-500 font-mono">POST {view?.url} · Authorization: Bearer &lt;token&gt;</div>
        <div className="flex justify-end gap-2">
          <Button size="sm" onClick={copy} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid={`${kind}-token-copy`}>📋 {t("Copy")}</Button>
          <Button size="sm" variant="outline" onClick={onClose} data-testid={`${kind}-token-close`}>{t("Done")}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function CredentialCard({ kind = "readonly" }) {
  const cfg = KINDS[kind];
  const { t } = useT();
  const { user } = useAuth();
  const qc = useQueryClient();
  const [label, setLabel] = useState(cfg.defaultLabel);
  const [secret, setSecret] = useState(null);
  const { data: st } = useQuery({ queryKey: ["mcp-connector"], queryFn: async () => (await api.get("/mcp-connector/status")).data, refetchInterval: 30_000 });
  const create = useMutation({
    mutationFn: () => api.post(cfg.path, { label }),
    onSuccess: (r) => { qc.invalidateQueries({ queryKey: ["mcp-connector"] }); setSecret(r.data); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed"),
  });
  const revoke = useMutation({
    mutationFn: () => api.delete(cfg.path),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["mcp-connector"] }); toast.success(t(cfg.revokedToast)); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed"),
  });
  const ro = st?.[kind];
  if (!ro) return null;
  const isSA = user.role === "super_admin";
  return (
    <div id={`${kind}-credential`} className="bg-white border border-slate-200 rounded-lg p-6 space-y-4" data-testid={`${kind}-credential-card`}>
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-2xl">{cfg.icon}</span>
        <h2 className="font-semibold text-lg">{t(cfg.title)} <span className="text-slate-400 font-normal text-sm">· {t(cfg.sub)}</span></h2>
        <Badge className={`border ${ro.active ? "bg-emerald-50 text-emerald-800 border-emerald-300" : ro.revoked_at ? "bg-red-50 text-red-700 border-red-200" : "bg-slate-100 text-slate-600 border-slate-300"}`} data-testid={`${kind}-status-badge`}>
          {ro.active ? t("ACTIVE") : ro.revoked_at ? t("REVOKED") : t("NONE")}
        </Badge>
        {ro.active && <Badge className="border border-slate-300 bg-white text-slate-700 font-mono text-[11px]" data-testid={`${kind}-hint`}>{ro.label} · {ro.hint}</Badge>}
      </div>
      <p className="text-sm text-slate-600">{t(cfg.blurb)}</p>
      <div className="rounded-md border border-slate-200 bg-slate-50 p-3 text-xs font-mono text-slate-700 space-y-1" data-testid={`${kind}-url-row`}>
        <div>POST <span className="text-slate-900">{ro.url}</span></div>
        <div className="text-slate-500">{ro.transport}</div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs text-slate-600" data-testid={`${kind}-meta`}>
        <div><div className="text-slate-400">{t("Created")}</div>{fmt(ro.created_at)}{ro.created_by ? ` · ${ro.created_by}` : ""}</div>
        <div><div className="text-slate-400">{t("Last call")}</div>{fmt(ro.last_used_at)}{ro.last_client ? ` · ${ro.last_client}` : ""}{kind === "filing" ? ` · ${ro.calls_total || 0} calls · ${ro.filed_total || 0} e-mails filed` : ""}</div>
        <div><div className="text-slate-400">{t("Revoked")}</div>{fmt(ro.revoked_at)}{ro.revoked_by ? ` · ${ro.revoked_by}` : ""}</div>
        <div><div className="text-slate-400">{t("Tools")}</div>{(ro.tools || []).join(" · ")}</div>
      </div>
      {isSA ? (
        <div className="flex flex-wrap items-center gap-2" data-testid={`${kind}-actions`}>
          {!ro.active && (
            <>
              <Label htmlFor={`${kind}-label`} className="text-xs">{t("Label")}</Label>
              <Input id={`${kind}-label`} value={label} onChange={(e) => setLabel(e.target.value)} className="h-9 w-56" data-testid={`${kind}-label-input`} />
              <Button size="sm" disabled={create.isPending} className="bg-emerald-600 hover:bg-emerald-700 text-white"
                      onClick={() => { if (window.confirm(t(cfg.confirmCreate))) create.mutate(); }}
                      data-testid={`${kind}-create-btn`}>
                {create.isPending ? t("Creating…") : `${cfg.icon} ${t(cfg.createLabel)}`}
              </Button>
            </>
          )}
          {ro.active && (
            <Button size="sm" variant="outline" disabled={revoke.isPending} className="text-red-700 border-red-200"
                    onClick={() => { if (window.confirm(t(cfg.confirmRevoke))) revoke.mutate(); }}
                    data-testid={`${kind}-revoke-btn`}>
              ✕ {revoke.isPending ? t("Revoking…") : t("Revoke")}
            </Button>
          )}
        </div>
      ) : (
        <div className="text-xs text-slate-500" data-testid={`${kind}-sa-only`}>{t("Only a Super Admin can create or revoke this credential.")}</div>
      )}
      <TokenOnceDialog secret={secret} onClose={() => setSecret(null)} t={t} cfg={cfg} kind={kind} />
    </div>
  );
}

export function ReadonlyCredentialCard() {
  return <CredentialCard kind="readonly" />;
}

export function FilingCredentialCard() {
  return <CredentialCard kind="filing" />;
}
